import React from 'react';
import ReactCountryFlag from 'react-country-flag';

// Map language codes to country codes (ISO 3166-1 alpha-2)
const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  // English variants
  en: 'US',  // Default to US flag for English

  // Major languages with clear country associations
  es: 'ES',  // Spanish - Spain
  fr: 'FR',  // French - France
  de: 'DE',  // German - Germany
  it: 'IT',  // Italian - Italy
  pt: 'PT',  // Portuguese - Portugal
  nl: 'NL',  // Dutch - Netherlands
  ru: 'RU',  // Russian - Russia
  pl: 'PL',  // Polish - Poland
  tr: 'TR',  // Turkish - Turkey
  ja: 'JP',  // Japanese - Japan
  ko: 'KR',  // Korean - South Korea
  zh: 'CN',  // Chinese - China

  // Languages with multiple country options
  ar: 'SA',  // Arabic - Saudi Arabia
  hi: 'IN',  // Hindi - India
  bn: 'BD',  // Bengali - Bangladesh
  ur: 'PK',  // Urdu - Pakistan
  fa: 'IR',  // Persian/Farsi - Iran
  he: 'IL',  // Hebrew - Israel

  // Southeast Asian languages
  th: 'TH',  // Thai - Thailand
  vi: 'VN',  // Vietnamese - Vietnam
  id: 'ID',  // Indonesian - Indonesia
  ms: 'MY',  // Malay - Malaysia
  fil: 'PH', // Filipino - Philippines
  tl: 'PH',  // Tagalog - Philippines

  // Nordic languages
  sv: 'SE',  // Swedish - Sweden
  no: 'NO',  // Norwegian - Norway
  da: 'DK',  // Danish - Denmark
  fi: 'FI',  // Finnish - Finland

  // Other European languages
  el: 'GR',  // Greek - Greece
  cs: 'CZ',  // Czech - Czech Republic
  hu: 'HU',  // Hungarian - Hungary
  ro: 'RO',  // Romanian - Romania
  uk: 'UA',  // Ukrainian - Ukraine

  // African languages
  sw: 'KE',  // Swahili - Kenya

  // Additional
  ta: 'IN',  // Tamil - India (could also be LK for Sri Lanka)
  te: 'IN',  // Telugu - India
};

// Map accent strings to country codes
const ACCENT_TO_COUNTRY: Record<string, string> = {
  'american': 'US',
  'british': 'GB',
  'australian': 'AU',
  'canadian': 'CA',
  'irish': 'IE',
  'scottish': 'GB',
  'welsh': 'GB',
  'new zealand': 'NZ',
  'south african': 'ZA',
  'indian': 'IN',

  'spanish': 'ES',
  'mexican': 'MX',
  'argentinian': 'AR',
  'chilean': 'CL',
  'colombian': 'CO',
  'peruvian': 'PE',
  'venezuelan': 'VE',

  'brazilian': 'BR',
  'portuguese': 'PT',

  'french': 'FR',
  'canadian french': 'CA',
  'belgian': 'BE',
  'swiss': 'CH',

  'german': 'DE',
  'austrian': 'AT',
  'swiss german': 'CH',

  'italian': 'IT',
  'russian': 'RU',
  'chinese': 'CN',
  'mandarin': 'CN',
  'cantonese': 'HK',
  'taiwanese': 'TW',
  'japanese': 'JP',
  'korean': 'KR',
  'arabic': 'SA',
  'egyptian': 'EG',
  'dutch': 'NL',
  'swedish': 'SE',
  'norwegian': 'NO',
  'danish': 'DK',
  'finnish': 'FI',
  'polish': 'PL',
  'turkish': 'TR',
  'greek': 'GR',
  'hebrew': 'IL',
  'hindi': 'IN',
  'thai': 'TH',
  'vietnamese': 'VN',
  'indonesian': 'ID',
  'malaysian': 'MY',
  'filipino': 'PH',
};

interface FlagIconProps {
  language?: string;
  accent?: string;
  size?: 'sm' | 'base' | 'lg';
  style?: React.CSSProperties;
  className?: string;
}

export const FlagIcon: React.FC<FlagIconProps> = ({
  language,
  accent,
  size = 'base',
  style,
  className = ''
}) => {
  // Determine country code
  let countryCode = 'US'; // Default to US

  // Parse language string - could be formats like:
  // - "en" (simple code)
  // - "EN American" (code + variant)
  // - "en-US" (code with locale)
  // - "English" (full name)

  if (language) {
    // Clean and parse the language string
    const langStr = language.trim();

    // Check if it's in format "XX Something" (e.g., "EN American", "FR Standard")
    const spaceMatch = langStr.match(/^([A-Z]{2,3})\s+(.+)$/i);
    if (spaceMatch) {
      const [, langCode, variant] = spaceMatch;
      const normalizedLang = langCode.toLowerCase();

      // Map the language code to its primary country
      if (LANGUAGE_TO_COUNTRY[normalizedLang]) {
        countryCode = LANGUAGE_TO_COUNTRY[normalizedLang];

        // Only use variant as accent if it's a meaningful accent (not "Standard")
        if (variant && accent === undefined && variant.toLowerCase() !== 'standard') {
          accent = variant; // Use variant as accent if no accent provided
        }
      } else {
        // Debug log unknown language codes
        console.log('Unknown language code in FlagIcon:', langCode, 'from', langStr);
      }
    } else {
      // Try standard parsing (hyphenated or simple codes)
      const langCode = langStr.toLowerCase().split(/[-_\s]/)[0];
      if (LANGUAGE_TO_COUNTRY[langCode]) {
        countryCode = LANGUAGE_TO_COUNTRY[langCode];
      }
    }
  }

  // Override with accent if provided (accent is more specific)
  if (accent) {
    const accentLower = accent.toLowerCase();

    // Skip generic terms that don't indicate a specific country
    const genericTerms = ['standard', 'default', 'general', 'neutral'];
    if (!genericTerms.includes(accentLower)) {
      const accentCountry = ACCENT_TO_COUNTRY[accentLower];
      if (accentCountry) {
        countryCode = accentCountry;
      } else {
        // Try partial match
        for (const [key, value] of Object.entries(ACCENT_TO_COUNTRY)) {
          if (accentLower.includes(key)) {
            countryCode = value;
            break;
          }
        }
      }
    }
  }

  // Size classes - circular with proper dimensions (increased sizes)
  const sizeStyles = {
    sm: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      objectFit: 'cover' as const
    },
    base: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      objectFit: 'cover' as const
    },
    lg: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      objectFit: 'cover' as const
    }
  };

  return (
    <div
      className={`inline-flex items-center justify-center overflow-hidden ${className}`}
      style={{
        ...sizeStyles[size],
        border: '1px solid rgba(0,0,0,0.1)',
        ...style
      }}
      title={`${language || 'Unknown'} ${accent ? `(${accent})` : ''}`}
    >
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        style={{
          width: '140%',
          height: '140%',
          objectFit: 'cover'
        }}
        aria-label={`Flag for ${language || 'Unknown'} ${accent ? `(${accent})` : ''}`}
      />
    </div>
  );
};

/**
 * Get country code from language and/or accent
 */
export function getCountryCode(language?: string, accent?: string): string {
  if (accent) {
    const accentLower = accent.toLowerCase();
    const accentCountry = ACCENT_TO_COUNTRY[accentLower];
    if (accentCountry) return accentCountry;

    // Try partial match
    for (const [key, value] of Object.entries(ACCENT_TO_COUNTRY)) {
      if (accentLower.includes(key)) {
        return value;
      }
    }
  }

  if (language) {
    const langCode = language.toLowerCase().split('-')[0];
    return LANGUAGE_TO_COUNTRY[langCode] || 'US';
  }

  return 'US';
}